import { useMemo, useRef, useState } from 'react'
import { pb } from '@/libs/pocketbase'
import { Badge } from '@journ/ui/badge'
import { Button } from '@journ/ui/button'
import {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@journ/ui/dropdown-menu'
import { Icons } from '@journ/ui/icons'
import { Input } from '@journ/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@journ/ui/popover'
import { Switch } from '@journ/ui/switch'
import { useDateFormat } from '@/features/preferences/hooks/use-date-format'
import { useIntlayer } from 'react-intlayer'
import { cn } from '@/libs/tv'
import { useVaultDocuments } from '../hooks/use-vault-documents'
import type { VaultDocument, VaultDocumentFilter, VaultDocumentUpload } from '../domain/document'

type VaultView = 'grid' | 'list'

const parseTags = (value: string) =>
	value
		.split(',')
		.map(tag => tag.trim())
		.filter(Boolean)

const formatSize = (size: number) => {
	if (size >= 1_000_000) return `${(size / 1_000_000).toFixed(1)} MB`
	if (size >= 1_000) return `${Math.round(size / 1_000)} KB`
	return `${size} B`
}

const fileExtension = (name: string) => name.split('.').pop()?.toUpperCase() ?? 'FILE'

type VaultSearchFilterProps = {
	query: string | null
	tag: string | null
	includeArchive: boolean
	availableTags: string[]
	onQueryChange: (value: string | null) => void
	onTagChange: (value: string | null) => void
	onIncludeArchiveChange: (value: boolean) => void
}

const VaultSearchFilter = ({
	query,
	tag,
	includeArchive,
	availableTags,
	onQueryChange,
	onTagChange,
	onIncludeArchiveChange,
}: VaultSearchFilterProps) => {
	const content = useIntlayer('vault-page')
	const [isOpen, setIsOpen] = useState(false)
	const hasFilters = Boolean(tag) || includeArchive

	return (
		<DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
			<div className='relative w-full md:max-w-95'>
				<Icons.Search size={16} className='absolute pointer-events-none left-3 top-2.75 text-muted-foreground' />
				<Input
					placeholder={content.searchPlaceholder.value}
					className='pl-9 pr-9 w-full'
					value={query ?? ''}
					onChange={event => onQueryChange(event.target.value || null)}
					autoComplete='off'
					autoCapitalize='none'
					autoCorrect='off'
					spellCheck={false}
				/>
				<DropdownMenuTrigger asChild>
					<button
						type='button'
						className={cn(
							'absolute z-10 right-3 top-2.5 opacity-50 transition-opacity duration-300 hover:opacity-100',
							hasFilters || isOpen ? 'opacity-100' : undefined,
						)}
					>
						<Icons.Filter size={16} />
					</button>
				</DropdownMenuTrigger>
			</div>

			<DropdownMenuContent className='w-65' align='start' sideOffset={8}>
				<div className='flex items-center justify-between px-2 py-1.5'>
					<span className='text-sm'>{content.includeArchived.value}</span>
					<Switch checked={includeArchive} onCheckedChange={onIncludeArchiveChange} />
				</div>
				<DropdownMenuSeparator />
				<DropdownMenuLabel>{content.card.tags.value}</DropdownMenuLabel>
				{availableTags.length > 0 ? (
					availableTags.map(item => (
						<DropdownMenuCheckboxItem
							key={item}
							checked={tag === item}
							onSelect={event => event.preventDefault()}
							onCheckedChange={() => onTagChange(tag === item ? null : item)}
						>
							{item}
						</DropdownMenuCheckboxItem>
					))
				) : (
					<DropdownMenuItem disabled>{content.filter.noTags.value}</DropdownMenuItem>
				)}
			</DropdownMenuContent>
		</DropdownMenu>
	)
}

type VaultViewSwitchProps = {
	view: VaultView
	onViewChange: (view: VaultView) => void
}

const VaultViewSwitch = ({ view, onViewChange }: VaultViewSwitchProps) => (
	<div className='flex gap-2 text-878787'>
		<Button
			variant='outline'
			size='icon'
			className={cn(view === 'grid' ? 'border-primary text-primary' : undefined)}
			onClick={() => onViewChange('grid')}
		>
			<Icons.GridView size={18} />
		</Button>
		<Button
			variant='outline'
			size='icon'
			className={cn(view === 'list' ? 'border-primary text-primary' : undefined)}
			onClick={() => onViewChange('list')}
		>
			<Icons.ListView size={18} />
		</Button>
	</div>
)

type VaultUploadButtonProps = {
	isMutating: boolean
	onUpload: (input: VaultDocumentUpload) => Promise<void>
}

const VaultUploadButton = ({ isMutating, onUpload }: VaultUploadButtonProps) => {
	const content = useIntlayer('vault-page')
	const inputRef = useRef<HTMLInputElement>(null)
	const [tags, setTags] = useState('')
	const [isOpen, setIsOpen] = useState(false)

	const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
		const file = event.target.files?.[0]
		if (!file) return
		await onUpload({ file, tags: parseTags(tags), customerId: null, workItemId: null })
		setTags('')
		setIsOpen(false)
		event.target.value = ''
	}

	return (
		<>
			<input ref={inputRef} id='upload-files' type='file' className='hidden' onChange={handleFileChange} />
			<Popover open={isOpen} onOpenChange={setIsOpen}>
				<PopoverTrigger asChild>
					<Button variant='outline' size='icon' disabled={isMutating}>
						<Icons.Add size={17} />
					</Button>
				</PopoverTrigger>
				<PopoverContent align='end' className='w-80'>
					<div className='flex flex-col gap-4'>
						<div className='flex flex-col gap-1'>
							<h2 className='text-sm font-medium'>{content.upload.title.value}</h2>
							<p className='text-xs text-606060'>{content.upload.description.value}</p>
						</div>
						<div className='flex flex-col gap-2'>
							<label htmlFor='vault-upload-tags' className='text-xs font-medium text-606060'>
								{content.upload.tags.value}
							</label>
							<Input
								id='vault-upload-tags'
								value={tags}
								onChange={event => setTags(event.target.value)}
								placeholder={content.upload.tagsPlaceholder.value}
							/>
						</div>
						<Button disabled={isMutating} onClick={() => inputRef.current?.click()}>
							<Icons.Files size={16} />
							{content.upload.action.value}
						</Button>
					</div>
				</PopoverContent>
			</Popover>
		</>
	)
}

type VaultHeaderProps = VaultSearchFilterProps &
	VaultViewSwitchProps & {
		isMutating: boolean
		onUpload: (input: VaultDocumentUpload) => Promise<void>
	}

const VaultHeader = ({
	query,
	tag,
	includeArchive,
	availableTags,
	view,
	isMutating,
	onQueryChange,
	onTagChange,
	onIncludeArchiveChange,
	onViewChange,
	onUpload,
}: VaultHeaderProps) => {
	return (
		<div className='py-6'>
			<div className='flex flex-col sm:flex-row sm:items-center justify-between gap-4'>
				<VaultSearchFilter
					query={query}
					tag={tag}
					includeArchive={includeArchive}
					availableTags={availableTags}
					onQueryChange={onQueryChange}
					onTagChange={onTagChange}
					onIncludeArchiveChange={onIncludeArchiveChange}
				/>
				<div className='flex items-center gap-2'>
					<VaultViewSwitch view={view} onViewChange={onViewChange} />
					<VaultUploadButton isMutating={isMutating} onUpload={onUpload} />
				</div>
			</div>
		</div>
	)
}

type VaultItemActionsProps = {
	document: VaultDocument
	tagsValue: string
	isMutating: boolean
	onSaveTags: () => Promise<void>
	onArchive: () => Promise<void>
}

const VaultItemActions = ({ document, tagsValue, isMutating, onSaveTags, onArchive }: VaultItemActionsProps) => {
	const content = useIntlayer('vault-page')

	const handleOpen = async (): Promise<void> => {
		const token = await pb.files.getToken()
		const base = pb.baseURL.replace(/\/$/, '')
		const separator = document.url.includes('?') ? '&' : '?'
		window.open(`${base}${document.url}${separator}token=${token}`, '_blank', 'noreferrer')
	}

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button variant='ghost' size='icon' className='size-8'>
					<Icons.MoreHoriz className='size-4' />
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align='end'>
				<DropdownMenuItem onClick={handleOpen}>
					{content.card.open.value}
				</DropdownMenuItem>
				<DropdownMenuItem disabled={isMutating || tagsValue.trim() === document.tags.join(', ')} onClick={onSaveTags}>
					{content.card.saveTags.value}
				</DropdownMenuItem>
				{document.status !== 'archived' && (
					<DropdownMenuItem disabled={isMutating} onClick={onArchive} className='text-destructive'>
						{content.card.archive.value}
					</DropdownMenuItem>
				)}
			</DropdownMenuContent>
		</DropdownMenu>
	)
}

type VaultItemProps = {
	document: VaultDocument
	onArchive: (documentId: string) => Promise<void>
	onUpdateTags: (documentId: string, tags: string[]) => Promise<void>
	isMutating: boolean
}

const VaultGridItem = ({ document, onArchive, onUpdateTags, isMutating }: VaultItemProps) => {
	const content = useIntlayer('vault-page')
	const { formatPreferenceDateTime } = useDateFormat()
	const [tagsValue, setTagsValue] = useState(document.tags.join(', '))
	const uploadedAt = formatPreferenceDateTime(document.uploadedAt)

	return (
		<div className='h-72 border relative flex text-muted-foreground p-4 flex-col gap-3 hover:bg-muted/40 transition-colors duration-200 group'>
			<div className='absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10'>
				<VaultItemActions
					document={document}
					tagsValue={tagsValue}
					isMutating={isMutating}
					onSaveTags={() => onUpdateTags(document.id, parseTags(tagsValue))}
					onArchive={() => onArchive(document.id)}
				/>
			</div>

			<div className='w-15 h-21 flex items-center justify-center relative border bg-background shadow-sm'>
				<div className='absolute inset-x-0 top-0 h-3 bg-muted' />
				<div className='flex flex-col items-center gap-1 pt-2'>
					<Icons.Files size={22} className='text-primary' />
					<span className='text-[9px] font-medium text-878787'>{fileExtension(document.name)}</span>
				</div>
			</div>

			<div className='flex flex-col text-left flex-1 min-h-0'>
				<div className='flex items-start justify-between gap-2 mb-2 mt-3'>
					<h2 className='text-sm text-primary line-clamp-1'>{document.name}</h2>
					{document.status === 'archived' && <Badge variant='outline'>{content.card.archived.value}</Badge>}
				</div>
				<p className='text-xs text-muted-foreground line-clamp-3'>
					{formatSize(document.size)}
					{uploadedAt ? ` · ${uploadedAt}` : ''}
				</p>
				<p className='text-xs text-muted-foreground line-clamp-2 mt-2'>
					{content.card.uploadedBy.value}: {document.uploadedBy || content.card.unknownUser.value}
				</p>
			</div>

			<div className='mt-auto space-y-3'>
				<div className='flex flex-wrap gap-1.5 min-h-6'>
					{document.tags.map(tag => (
						<Badge key={tag} variant='secondary' className='text-xxs'>
							{tag}
						</Badge>
					))}
				</div>
				<Input
					value={tagsValue}
					onChange={event => setTagsValue(event.target.value)}
					placeholder={content.upload.tagsPlaceholder.value}
					className='h-8 text-xs'
				/>
			</div>
		</div>
	)
}

const VaultListItem = ({ document, onArchive, onUpdateTags, isMutating }: VaultItemProps) => {
	const content = useIntlayer('vault-page')
	const { formatPreferenceDateTime } = useDateFormat()
	const uploadedAt = formatPreferenceDateTime(document.uploadedAt)

	return (
		<div className='border flex items-center gap-4 p-3 text-muted-foreground hover:bg-muted/40 transition-colors duration-200'>
			<div className='w-9 h-11 flex items-center justify-center relative border bg-background shadow-sm shrink-0'>
				<div className='absolute inset-x-0 top-0 h-1.5 bg-muted' />
				<Icons.Files size={16} className='text-primary' />
			</div>

			<div className='flex flex-col min-w-0 flex-1'>
				<div className='flex items-center gap-2'>
					<h2 className='text-sm text-primary truncate'>{document.name}</h2>
					{document.status === 'archived' && <Badge variant='outline'>{content.card.archived.value}</Badge>}
				</div>
				<p className='text-xs text-muted-foreground truncate'>
					{formatSize(document.size)}
					{uploadedAt ? ` · ${uploadedAt}` : ''} · {document.uploadedBy || content.card.unknownUser.value}
				</p>
			</div>

			<div className='hidden md:flex flex-wrap gap-1.5 max-w-xs justify-end'>
				{document.tags.map(tag => (
					<Badge key={tag} variant='secondary' className='text-xxs'>
						{tag}
					</Badge>
				))}
			</div>

			<VaultItemActions
				document={document}
				tagsValue={document.tags.join(', ')}
				isMutating={isMutating}
				onSaveTags={() => onUpdateTags(document.id, document.tags)}
				onArchive={() => onArchive(document.id)}
			/>
		</div>
	)
}

const VaultEmptyState = ({ hasFilters }: { hasFilters: boolean }) => {
	const content = useIntlayer('vault-page')
	return (
		<div className='border border-dashed min-h-80 flex items-center justify-center text-center p-8'>
			<div className='max-w-sm space-y-2'>
				<h2 className='text-lg font-medium'>
					{hasFilters ? content.noResults.title.value : content.empty.title.value}
				</h2>
				<p className='text-sm text-606060'>
					{hasFilters ? content.noResults.description.value : content.empty.description.value}
				</p>
			</div>
		</div>
	)
}

const VaultPage = () => {
	const content = useIntlayer('vault-page')
	const [query, setQuery] = useState<string | null>(null)
	const [tag, setTag] = useState<string | null>(null)
	const [includeArchive, setIncludeArchive] = useState(false)
	const [view, setView] = useState<VaultView>('grid')
	const filter = useMemo<VaultDocumentFilter>(() => ({ q: query, tag, includeArchive }), [query, tag, includeArchive])
	const { documents, isLoading, isMutating, upload, archive, updateTags } = useVaultDocuments(filter)
	const hasFilters = Boolean(query || tag || includeArchive)

	const availableTags = useMemo(() => {
		const all = documents.flatMap(document => document.tags)
		return Array.from(new Set(all)).sort()
	}, [documents])

	return (
		<>
			<VaultHeader
				query={query}
				tag={tag}
				includeArchive={includeArchive}
				availableTags={availableTags}
				view={view}
				isMutating={isMutating}
				onQueryChange={setQuery}
				onTagChange={setTag}
				onIncludeArchiveChange={setIncludeArchive}
				onViewChange={setView}
				onUpload={upload}
			/>

			{isLoading ? (
				<div className='text-sm text-606060'>{content.loading.value}</div>
			) : documents.length === 0 ? (
				<VaultEmptyState hasFilters={hasFilters} />
			) : view === 'grid' ? (
				<div
					className={cn('grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 3xl:grid-cols-6 gap-8 pb-20')}
				>
					{documents.map(document => (
						<VaultGridItem
							key={document.id}
							document={document}
							onArchive={archive}
							onUpdateTags={updateTags}
							isMutating={isMutating}
						/>
					))}
				</div>
			) : (
				<div className='flex flex-col gap-2 pb-20'>
					{documents.map(document => (
						<VaultListItem
							key={document.id}
							document={document}
							onArchive={archive}
							onUpdateTags={updateTags}
							isMutating={isMutating}
						/>
					))}
				</div>
			)}
		</>
	)
}

export { VaultPage }
